import { FC, useState, useEffect } from 'react'
import { useAuth } from '../../auth'
import { toast } from 'react-toastify'
import {
  updateSubAccount,
  getCompanyGroupAdmins,
  getAccountingAccountsWithParams,
} from '../core/_requests'
import {
  AccountingAccount,
  CompanyGroupAdmin,
  SubAccount,
} from '../core/_models'
import { useProcurementPermissions } from '../core/permissionAccess'
import { PROCUREMENT_PERMS } from '../core/permissions'
import { libraryUiScope } from '../core/procurementLibraryUiScope'
import { procurementTenantCgaId } from '../core/procurementTenantContext'
import SearchableSelect from '../../../shared/components/SearchableSelect'

interface EditSubAccountingModalProps {
  visible: boolean
  onClose: () => void
  onSubmit: () => void
  subAccount: SubAccount | null
}

const EditSubAccountingModal: FC<EditSubAccountingModalProps> = ({ visible, onClose, onSubmit, subAccount }) => {
  const { currentUser } = useAuth()
  const { getScope } = useProcurementPermissions()
  const libraryScope = libraryUiScope(
    getScope,
    PROCUREMENT_PERMS.LIBRARY_SUBACCOUNTING_MANAGE,
    PROCUREMENT_PERMS.LIBRARY_SUBACCOUNTING_VIEW,
  )
  const libraryScopeAll = libraryScope === 'ALL'
  const libraryScopeCompany = libraryScope === 'COMPANY'
  const tenantCgaId = procurementTenantCgaId(currentUser)

  const [formData, setFormData] = useState({
    company: '',
    subaccountCodes: '',
    accountCodeId: '',
  })
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [accountingCodes, setAccountingCodes] = useState<AccountingAccount[]>([])
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false)

  // New state for company dropdown
  const [companies, setCompanies] = useState<CompanyGroupAdmin[]>([])
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(false)

  // Load sub account data when modal opens
  useEffect(() => {
    if (visible && subAccount) {
      setFormData({
        company: subAccount.cgaid?.id?.toString() || '',
        subaccountCodes: subAccount.subaccountCodes || '',
        accountCodeId: subAccount.accountCodeId?.toString() || '',
      })
    }
  }, [visible, subAccount])

  // Load companies when modal opens (only for superadmin)
  useEffect(() => {
    if (visible && libraryScopeAll) {
      loadCompanies()
    }
  }, [visible, libraryScopeAll])

  // Load accounting codes when company changes
  useEffect(() => {
    if (visible) {
      loadAccountingCodes()
    }
  }, [visible, formData.company])

  const loadCompanies = async () => {
    setIsLoadingDropdowns(true)
    try {
      const companiesData = await getCompanyGroupAdmins()
      setCompanies(companiesData)
    } catch (error) {
      console.error('Error loading companies:', error)
    } finally {
      setIsLoadingDropdowns(false)
    }
  }

  const loadAccountingCodes = async () => {
    setIsLoadingAccounts(true)
    try {
      // For superadmin, require company
      if (libraryScopeAll) {
        if (!formData.company) {
          setAccountingCodes([])
          setIsLoadingAccounts(false)
          return
        }
      }

      let cgaIdToUse: number | undefined
      if (libraryScopeAll && formData.company) {
        cgaIdToUse = parseInt(formData.company, 10)
      } else {
        cgaIdToUse = tenantCgaId
      }
      if (!cgaIdToUse) {
        setAccountingCodes([])
        setIsLoadingAccounts(false)
        return
      }

      const data = await getAccountingAccountsWithParams(cgaIdToUse)
      setAccountingCodes(data)
    } catch (error) {
      console.error('Error loading accounting codes:', error)
      setAccountingCodes([])
    } finally {
      setIsLoadingAccounts(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}

    // Company validation for superadmin
    if (libraryScopeAll && !formData.company) {
      newErrors.company = 'Company is required'
    }

    if (!formData.subaccountCodes.trim()) newErrors.subaccountCodes = 'Sub Account Code is required'
    if (!formData.accountCodeId) newErrors.accountCodeId = 'Account Code is required'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm() || !subAccount) return
    setIsSubmitting(true)
    try {
      await updateSubAccount(subAccount.id, {
        subaccountCodes: formData.subaccountCodes,
        accountCodeId: parseInt(formData.accountCodeId),
      })
      toast.success('Sub Accounting updated successfully!')
      onSubmit()
      handleClose()
    } catch (error: any) {
      console.error('Error updating sub accounting:', error)
      toast.error('Failed to update sub accounting')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setFormData({
      company: '',
      subaccountCodes: '',
      accountCodeId: '',
    })
    setErrors({})
    setAccountingCodes([])
    setCompanies([])
    onClose()
  }

  if (!visible || !subAccount) return null

  return (
    <div className="modal fade show d-flex align-items-center justify-content-center" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 1050 }}>
      <div className='modal-dialog modal-lg modal-dialog-centered' role='document'>
        <div className='modal-content bg-white' style={{ color: '#181C32' }}>
          <div className='modal-header'>
            <h5 className='modal-title'>Edit Sub Accounting</h5>
            <button type='button' className='btn-close' onClick={handleClose}></button>
          </div>
          <div className='modal-body'>
            <div className='row g-3'>

              {/* Company dropdown - only for superadmin */}
              {libraryScopeAll && (
                <div className='col-md-6'>
                  <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Company</label>
                  <SearchableSelect
                    className={`form-select ${errors.company ? 'is-invalid' : ''}`}
                    name='company'
                    value={formData.company}
                    onChange={handleInputChange}
                    disabled={isLoadingDropdowns}
                  >
                    <option value=''>Select Company</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>{company.name}</option>
                    ))}
                  </SearchableSelect>
                  {errors.company && <div className='invalid-feedback'>{errors.company}</div>}
                </div>
              )}

              <div className='col-12'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Sub Account Code</label>
                <input
                  type='text'
                  className={`form-control ${errors.subaccountCodes ? 'is-invalid' : ''}`}
                  name='subaccountCodes'
                  value={formData.subaccountCodes}
                  onChange={handleInputChange}
                  placeholder='e.g., 6000-EXP-01-01'
                  style={{ color: '#000' }}
                />
                {errors.subaccountCodes && <div className='invalid-feedback'>{errors.subaccountCodes}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Account Code</label>
                <SearchableSelect
                  className={`form-select ${errors.accountCodeId ? 'is-invalid' : ''}`}
                  name='accountCodeId'
                  value={formData.accountCodeId}
                  onChange={handleInputChange}
                  disabled={isLoadingAccounts || (libraryScopeAll && !formData.company)}
                >
                  <option value=''>
                    {isLoadingAccounts ? 'Loading account codes...' :
                      (libraryScopeAll && !formData.company) ? 'Please select a company first' :
                        'Select Account Code'}
                  </option>
                  {accountingCodes.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.accountCodes}
                    </option>
                  ))}
                </SearchableSelect>
                {errors.accountCodeId && <div className='invalid-feedback'>{errors.accountCodeId}</div>}
              </div>

              {/* Show current CGA info for Company Group Admin users */}
              {libraryScopeCompany && tenantCgaId && (
                <div className='col-12'>
                  <div className='alert alert-info'>
                    <i className='fas fa-info-circle me-2'></i>
                    Sub accounting will be updated under your Company Group Admin (ID: {tenantCgaId})
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className='modal-footer'>
            <button type='button' className='btn btn-light btn-sm' onClick={handleClose} disabled={isSubmitting}>Cancel</button>
            <button type='button' className='btn btn_primary' onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Updating...' : 'Update Sub Accounting'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EditSubAccountingModal
